import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { Button } from 'src/components/design-system';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { FileRow } from './file-row';
import styles from './select-tabs-step.module.scss';

const COLLAPSED_TAB_COUNT = 10;

type SelectTabsStepProps = {
  fileName: string;
  fileSize: number;
  isReadingFile: boolean;
  sheetNames: string[];
  selectedTabs: string[];
  onChangeFile: (file: File) => void;
  onToggleTab: (sheetName: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  onBack: () => void;
  onReview: () => void;
};

export const SelectTabsStep = ({
  fileName,
  fileSize,
  isReadingFile,
  sheetNames,
  selectedTabs,
  onChangeFile,
  onToggleTab,
  onSelectAll,
  onClear,
  onBack,
  onReview,
}: SelectTabsStepProps) => {
  const lang = useSelector(getLang('SETTINGS'));
  const [isExpanded, setIsExpanded] = useState(false);

  const selected = useMemo(() => new Set(selectedTabs), [selectedTabs]);
  const visibleTabs = isExpanded ? sheetNames : sheetNames.slice(0, COLLAPSED_TAB_COUNT);
  const hiddenCount = sheetNames.length - visibleTabs.length;

  return (
    <div>
      <FileRow
        fileName={fileName}
        fileSize={fileSize}
        isBusy={isReadingFile}
        onChangeFile={onChangeFile}
      />

      <h2 className={styles.heading}>{lang.SHIFTS_CHOOSE_TABS}</h2>
      <span className={styles.hint}>{lang.SHIFTS_CHOOSE_TABS_HINT}</span>

      <div className={styles.counterRow}>
        <span className={styles.counter}>
          {lang.SHIFTS_TABS_SELECTED.replace(
            '{selected}',
            formatNumber(selectedTabs.length),
          ).replace('{total}', formatNumber(sheetNames.length))}
        </span>
        <div className={styles.counterActions}>
          <button type="button" className={styles.linkPrimary} onClick={onSelectAll}>
            {lang.SHIFTS_SELECT_ALL}
          </button>
          <button type="button" className={styles.linkMuted} onClick={onClear}>
            {lang.SHIFTS_CLEAR}
          </button>
        </div>
      </div>

      <ListItemsNavigator
        orientation="horizontal"
        role="group"
        aria-label={lang.SHIFTS_CHOOSE_TABS}
        className={styles.chipGrid}>
        {visibleTabs.map(sheetName => {
          const isSelected = selected.has(sheetName);

          return (
            <button
              key={sheetName}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              onClick={() => onToggleTab(sheetName)}
              className={classNames(styles.chip, isSelected && styles.chipSelected)}>
              <span className={styles.chipCheck}>
                <FontAwesomeIcon icon={faCheck} />
              </span>
              {sheetName}
            </button>
          );
        })}

        {hiddenCount > 0 && (
          <button type="button" className={styles.expander} onClick={() => setIsExpanded(true)}>
            {lang.SHIFTS_SHOW_ALL_TABS.replace('{count}', formatNumber(sheetNames.length))}
          </button>
        )}
      </ListItemsNavigator>

      <div className={styles.footer}>
        <Button type="default" onClick={onBack}>
          <FontAwesomeIcon icon={faChevronLeft} className={styles.buttonIconLeft} />
          {lang.SHIFTS_BACK}
        </Button>
        <Button
          type="primary"
          disabled={isReadingFile || selectedTabs.length === 0}
          onClick={onReview}>
          {lang.SHIFTS_REVIEW_IMPORT}
          <FontAwesomeIcon icon={faChevronRight} className={styles.buttonIconRight} />
        </Button>
      </div>
    </div>
  );
};
